import { Box, Button, Dialog, Flex, Link as CLink, List, Portal, Stack, Text } from '@chakra-ui/react'
import { LuDownload, LuTrash2 } from 'react-icons/lu'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link as RouterLink } from 'react-router'
import { api, isApiError } from '~/api/client'
import { useSignInSettings } from '~/api/hooks'
import { FreshSignIn } from '~/components/member/FreshSignIn'
import { Illustration } from '~/components/member/Illustration'
import { ErrorAlert, useFriendlyError } from '~/components/member/feedback'
import { MemberSection, PageTitle } from '~/components/member/layout'
import { useT } from '~/i18n/use-t'
import { MemberSectionGuard } from './MemberGuard'
import { useLogout } from './MemberShell'

/** Saves the member's own data as a JSON file in the browser (Art. 15 / 20). Nothing here touches browser storage. */
function useDownload() {
  const [done, setDone] = useState(false)
  const run = useMutation({
    mutationFn: async () => {
      const data = await api('memberExport')
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }))
      try {
        const a = document.createElement('a')
        a.href = url
        a.download = 'my-data.json'
        document.body.appendChild(a)
        a.click()
        a.remove()
      } finally {
        URL.revokeObjectURL(url)
      }
      setDone(true)
    },
  })
  return { run, done }
}

/**
 * "Withdraw my document consent" (identity-document spec §10.1, §11; final gate F3): the member can withdraw the
 * consent to the document check here, behind a confirmation. The server deletes the detailed results (or, before the
 * first decision, ends the document work and a person calls); the record of the check and the first name stay.
 */
function DocumentWithdraw({ available }: { available: boolean }) {
  const { t } = useT()
  const friendly = useFriendlyError()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [done, setDone] = useState(false)
  const send = useMutation({
    mutationFn: () => api('memberDocumentWithdraw'),
    onSuccess: () => {
      setOpen(false)
      setDone(true)
      void qc.invalidateQueries()
    },
  })
  // The "done" line stays after the refetch (which no longer offers the withdrawal).
  if (!available && !done) return null
  if (done)
    return (
      <Box borderTop='1px solid' borderColor='border' pt={3} mt={3}>
        <Text role='status'>{t('dashboard.documentWithdraw.done')}</Text>
      </Box>
    )
  return (
    <Stack gap={3} borderTop='1px solid' borderColor='border' pt={3} mt={3}>
      <Text fontWeight='bold'>{t('dashboard.documentWithdraw.title')}</Text>
      <Text>{t('dashboard.documentWithdraw.text')}</Text>
      <Button variant='outline' alignSelf={{ base: 'stretch', sm: 'flex-start' }} onClick={() => setOpen(true)}>
        {t('dashboard.documentWithdraw.button')}
      </Button>
      <Dialog.Root
        open={open}
        onOpenChange={(e) => {
          if (!e.open && !send.isPending) {
            setOpen(false)
            send.reset()
          }
        }}
        role='alertdialog'
        placement='center'
        scrollBehavior='inside'
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner p={4}>
            <Dialog.Content w='full' maxW='md' maxH='calc(100dvh - 2rem)' my={0}>
              <Dialog.Header>
                <Dialog.Title overflowWrap='anywhere' minW={0}>
                  {t('dashboard.documentWithdraw.confirmTitle')}
                </Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                <Stack gap={3}>
                  <Text>{t('dashboard.documentWithdraw.confirmText')}</Text>
                  {send.isError && <ErrorAlert>{friendly(send.error)}</ErrorAlert>}
                </Stack>
              </Dialog.Body>
              <Dialog.Footer flexDirection='column-reverse' alignItems='stretch' gap={2}>
                <Button variant='outline' disabled={send.isPending} onClick={() => setOpen(false)}>
                  {t('dashboard.documentWithdraw.cancel')}
                </Button>
                <Button loading={send.isPending} onClick={() => send.mutate()}>
                  {t('dashboard.documentWithdraw.confirm')}
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Stack>
  )
}

/**
 * The download needs a sign-in of the last 10 minutes (passwordless P2: the file holds the face template). A 403
 * `fresh_sign_in_required` offers the sign-in email here; the emailed code signs in again on this page.
 */
function DownloadFresh({ email, onSignedIn }: { email: string | null; onSignedIn: () => void }) {
  const { t } = useT()
  const settings = useSignInSettings()
  const logout = useLogout()
  return (
    <FreshSignIn
      email={email}
      mode={settings.data?.mode}
      intro={t('dashboard.downloadFresh')}
      onSignedIn={onSignedIn}
      onSignOut={logout ? () => logout.mutate() : undefined}
      signingOut={logout?.isPending}
      formId='download-code-form'
    />
  )
}

/** `/me/data`: what we keep, download my data, withdraw the document consent, delete my data. */
export default function Data() {
  const { t } = useT()
  const friendly = useFriendlyError()
  const download = useDownload()
  const [signedInAgain, setSignedInAgain] = useState(false)
  const needsFresh = download.run.isError && isApiError(download.run.error, 'fresh_sign_in_required')
  return (
    <MemberSectionGuard>
      {({ member }) => (
        <Flex gap={8} align='flex-start' direction={{ base: 'column', lg: 'row' }}>
          {/* The page is one section: a raised card from `md`, no frame on a phone (as Write to us and Password). */}
          <MemberSection frameFrom='md' maxW='3xl' flex='1' w='full'>
            <Stack gap={4}>
              <PageTitle title={t('dashboard.myData')} />
              <Stack gap={3}>
                <Text fontWeight='bold'>{t('dashboard.keepsTitle')}</Text>
                <List.Root ps={6} gap={1}>
                  {member.keeps.map((k) => (
                    <List.Item key={k}>{t(`dashboard.keeps.${k}`)}</List.Item>
                  ))}
                </List.Root>
                {download.run.isError && !needsFresh && <ErrorAlert>{friendly(download.run.error)}</ErrorAlert>}
                {download.done && <Text role='status'>{t('dashboard.downloadDone')}</Text>}
                {signedInAgain && !download.done && <Text role='status'>{t('dashboard.downloadAgain')}</Text>}
                {/* Download is the main action; Delete is a quieter, red secondary action set apart under a rule, never a
                  twin of Download next to it (mobile review). */}
                <Button
                  variant='outline'
                  alignSelf={{ base: 'stretch', sm: 'flex-start' }}
                  loading={download.run.isPending}
                  onClick={() => download.run.mutate()}
                >
                  <LuDownload aria-hidden />
                  {t('dashboard.download')}
                </Button>
                {needsFresh && (
                  <DownloadFresh
                    email={member.email}
                    onSignedIn={() => {
                      download.run.reset()
                      setSignedInAgain(true)
                    }}
                  />
                )}
                <DocumentWithdraw available={member.documentWithdrawable === true} />
                <Box borderTop='1px solid' borderColor='border' pt={3} mt={3}>
                  <CLink
                    asChild
                    display='inline-flex'
                    alignItems='center'
                    gap={2}
                    minH='12'
                    color='fg.error'
                    textDecoration='underline'
                    _hover={{ color: 'fg.error' }}
                  >
                    <RouterLink to='/me/delete'>
                      <LuTrash2 aria-hidden />
                      {t('dashboard.deleteLink')}
                    </RouterLink>
                  </CLink>
                </Box>
              </Stack>
            </Stack>
          </MemberSection>
          <Illustration name='shield' w={28} h={28} hideBelow='lg' />
        </Flex>
      )}
    </MemberSectionGuard>
  )
}
